import React, { useMemo, useState, useEffect } from 'react';
import { parse } from 'date-fns';

import DatePicker from 'components/DatePicker';
import Section from 'components/Meteorology/Shared/ControlPanelSheet/Section';
import usePeriodType from 'hooks/Meteorology/Fields/ModelPerformance/usePeriodType';
import useOptions from 'hooks/Meteorology/Fields/ModelPerformance/useOptions';
import usePresetRounds from 'hooks/Meteorology/Fields/ModelPerformance/usePresetRounds';

export default function RoundsSection({
  open,
  onToggle,
}: {
  open: boolean;
  onToggle: () => void;
}) {
  const { periodType } = usePeriodType();
  const { availableRounds, minDate, maxDate } = useOptions();
  const {
    selectedRounds, setSelectedRounds, roundsRange, setRoundsRange,
  } = usePresetRounds();

  const [localStartDate, setLocalStartDate] = useState<Date | null>(null);
  const [localEndDate, setLocalEndDate] = useState<Date | null>(null);

  const minDateObj = useMemo(
    () => (minDate ? parse(minDate, 'yyyy-MM-dd', new Date()) : undefined),
    [minDate],
  );
  const maxDateObj = useMemo(
    () => (maxDate ? parse(maxDate, 'yyyy-MM-dd', new Date()) : undefined),
    [maxDate],
  );

  useEffect(() => {
    if (periodType !== 'preset') {
      setLocalStartDate(null);
      setLocalEndDate(null);
    }
  }, [periodType]);

  useEffect(() => {
    if (selectedRounds.length === 0) return;
    const times = selectedRounds.map((d) => d.getTime());
    setLocalStartDate(new Date(Math.min(...times)));
    setLocalEndDate(new Date(Math.max(...times)));
  }, [selectedRounds]);

  // O intervalo escolhido fica no Redux e sobrevive à troca de pêntada: o calendário
  // mostra ele, não só o trecho que tem rodadas na pêntada atual.
  const startDate = useMemo(() => {
    if (roundsRange) return parse(roundsRange.start, 'yyyy-MM-dd', new Date());
    if (localStartDate) return localStartDate;
    if (!selectedRounds.length) return null;
    return new Date(Math.min(...selectedRounds.map((d) => d.getTime())));
  }, [roundsRange, localStartDate, selectedRounds]);

  const endDate = useMemo(() => {
    if (roundsRange) return parse(roundsRange.end, 'yyyy-MM-dd', new Date());
    if (localEndDate) return localEndDate;
    if (!selectedRounds.length) return null;
    return new Date(Math.max(...selectedRounds.map((d) => d.getTime())));
  }, [roundsRange, localEndDate, selectedRounds]);

  const handleDateChange = (dates: (Date | null)[] | Date | null) => {
    if (!Array.isArray(dates)) {
      setLocalStartDate(null);
      setLocalEndDate(null);
      setSelectedRounds([]);
      setRoundsRange(null, null);
      return;
    }
    const [newStartDate, newEndDate] = dates;
    setLocalStartDate(newStartDate);
    setLocalEndDate(newEndDate);
    setRoundsRange(newStartDate, newEndDate);

    if (newStartDate && newEndDate) {
      const startTime = newStartDate.getTime();
      const endTime = newEndDate.getTime();
      const inRange = (availableRounds ?? [])
        .map((roundStr) => parse(roundStr, 'yyyy-MM-dd', new Date()))
        .filter((d) => d.getTime() >= startTime && d.getTime() <= endTime);
      setSelectedRounds(inRange);
    } else {
      setSelectedRounds([]);
    }
  };

  const valueText = selectedRounds.length > 0 ? `${selectedRounds.length} rodada(s)` : undefined;

  return (
    <Section label="Rodadas" valueText={valueText} open={open} onToggle={onToggle}>
      <DatePicker
        selectsRange
        startDate={startDate}
        endDate={endDate}
        onChange={handleDateChange}
        minDate={minDateObj}
        maxDate={maxDateObj}
        inline
        containerClass="ds-scroll-datepicker"
      />
    </Section>
  );
}
